import {
  Box,
  Chip,
  FormHelperText,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  TextField,
  Popper,
  Paper,
  ClickAwayListener,
} from "@mui/material";
import React, {
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AppliedTaxes,
  DefaultTax,
  Descriptions,
  initialServiceData,
  ServiceData,
  serviceValues as initialServiceValues,
  ServiceValues,
  typeTaxEnum,
} from "../../../../types/budget/service-item";
import { ExchangeData } from "../../../../types/budget/budget";
import { LabeledInput } from "../../../UI/LabeledInput";
import { createOptionsFromEnum } from "../../../../utils/formatters";
import { formatCurrencyValue } from "../../../../utils/currencySymbol";
import { ServiceOptions } from "../../../../utils/serviceTypesFromBudget";
import {
  useBudgetActions,
  useBudgetMeta,
} from "../../../../contexts/BudgetContext/projectbudgets.context";
import { useProject } from "../../../../contexts/project.context";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { BsThreeDotsVertical } from "react-icons/bs";
import { IoIosCloseCircle } from "react-icons/io";
import {
  MdCardGiftcard,
  MdExpandLess,
  MdExpandMore,
  MdOutlineSwapHoriz,
  MdPerson,
  MdWarning,
} from "react-icons/md";
import UnitValueInput from "../../UnitValueInput";
import { formatUnitValue } from "../../../../utils/unitValue";
import { TabActive } from "../../../../types";
import { ReactComponent as DescriptionIcon } from "../../../../assets/styled-icons/Description.svg";
import { ReactComponent as ApplicationPlaceIcon } from "../../../../assets/styled-icons/ApplicationPlace.svg";
import { ReactComponent as PercentIcon } from "../../../../assets/styled-icons/budget/material-symbols_percent.svg";
import { ReactComponent as ViewObsIcon } from "../../../../assets/styled-icons/ViewObs.svg";
import { ReactComponent as WriteObsIcon } from "../../../../assets/styled-icons/WriteObs.svg";
import DescriptionModal from "../../../Project/components/DescriptionModal";
import ApplicationPlaceModal from "../../../Project/components/ApplicationPlaceModal";
import TaxesModal from "../../../Project/components/TaxesModal";
import ObservationsModal from "../../../Project/components/ObservationsModal";
import { FiCheckSquare, FiUserCheck } from "react-icons/fi";
import { LabeledInputFull } from "../../../UI/LabeledInputFull";
import { hasCustomTaxes } from "../../../../utils/utils";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";

type Props = {
  title: string;
  serviceData: ServiceData;
  type: string;
  index: number;
  currency: string;
  categoryExchange?: ExchangeData;
  descriptionFocus: any;
  setDescriptionFocus: any;
  serviceFocus: any;
  setServiceFocus: any;
  mainAppliedTaxes?: AppliedTaxes;
  /**
   * Abre o modal de transferência para outro fornecedor. Ausente ⇒ o item de
   * menu não é exibido. O modal vive no `BudgetItem` de propósito: este leaf não
   * consome `supplierList`/`providersById` para não re-renderizar a cada edição
   * em serviço vizinho (ver comentário em `useBudgetMeta`, abaixo).
   */
  onTransferService?: (serviceData: ServiceData) => void;
};

export const FREE_SERVICE_FIELD = [
  "EQUIPMENTS",
  "ATTRACTION",
  "BRINDES_INCENTIVOS_PREMIACOES",
  "CENOGRAPHY_DECORATION",
  "COMMUNICATIONS",
  "CONTENT",
  "FOTO_VIDEO",
  "LICENCES_TAXES",
  "GRAPHIC_MATERIAL",
  "MOUNTING_COMPANY",
  "NECESSARY_ADDITIONS",
  "HUMAN_RESOURCES_STAFF",
  "EVENT_INSURANCE",
  "TECHNOLOGY",
  "TRAVEL_INSURANCE",
  "SERVICE_SUPPORT",
];

export function useTextareaOverflow(
  inputRef: React.RefObject<HTMLTextAreaElement>,
  value: string | undefined
) {
  const [hasOverflow, setHasOverflow] = useState(false);

  const recompute = () => {
    const el = inputRef.current;
    if (!el) return;

    const cs = window.getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight || "0");
    if (!lineHeight) {
      setHasOverflow(false);
      return;
    }
    const paddingY =
      parseFloat(cs.paddingTop || "0") + parseFloat(cs.paddingBottom || "0");
    const contentHeight = el.scrollHeight - paddingY;
    const lines = contentHeight / lineHeight;

    setHasOverflow(lines > 1.05);
  };

  useLayoutEffect(recompute, [inputRef, value]);
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [inputRef]);

  return hasOverflow;
}

export const daysBetweenInclusive = (
  start: Date,
  end: Date,
  serviceType?: string
) => {
  const s = startOfDayTs(start);
  const e = startOfDayTs(end);

  const diffDays = Math.round((e - s) / (1000 * 60 * 60 * 24));

  if (serviceType === "ACCOMMODATIONS") {
    return Math.max(1, diffDays);
  } else {
    return Math.max(1, diffDays + 1);
  }
};

export const formatBRDate = (d: Date) => {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

export const startOfDayTs = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

export const parseBRDate = (text: string): Date | null => {
  const clean = text.replace(/[^\d]/g, "");
  if (clean.length !== 8) return null;
  const day = parseInt(clean.substring(0, 2), 10);
  const month = parseInt(clean.substring(2, 4), 10) - 1;
  const year = parseInt(clean.substring(4, 8), 10);
  const date = new Date(year, month, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  )
    return null;
  return date;
};

export const applyDateMask = (value: string): string => {
  const digits = value.replace(/[^\d]/g, "").substring(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

// Builds the ServiceData payload for a `UPDATE_CALC_VALUES` / `UPDATE_SERVICE`
// dispatch using shallow spreads. The reducer treats the payload as immutable
// (it just re-inserts it at the right index in `budgetServices`), so the
// previous defensive `JSON.parse(JSON.stringify(...))` over the whole service
// + every non-active negotiationValue entry was wasted work on every keystroke.
type ServiceValuesKey = "budgetValues" | "negotiationValue" | "closingValues";
type ServiceValuesField = keyof ServiceValues;

const SERVICE_METADATA_FIELDS = new Set<ServiceValuesField>([
  "observation",
  "internalObservation",
  "serviceType",
  "descriptions",
  "serviceApplication",
  "inputDate",
  "outDate",
]);

function isServiceMetadataField(field: ServiceValuesField): boolean {
  return SERVICE_METADATA_FIELDS.has(field);
}

function buildServiceValuesDraft(
  values: ServiceValues,
  baseValues: ServiceValues
): Partial<ServiceValues> {
  const draft: Partial<ServiceValues> = {};
  (Object.keys(values) as ServiceValuesField[]).forEach((key) => {
    if (values[key] !== baseValues[key]) {
      draft[key] = values[key] as any;
    }
  });
  return draft;
}

/**
 * Merge local edits onto the canonical step slice.
 *
 * `taxData` is special: the draft must keep tariff *inputs* (`value` /
 * `typeTax`) for responsive typing, but must NOT replace the whole
 * `taxData` tree — that shadows `currencyValue` / sibling tax totals from
 * `UPDATE_CALC_VALUES` and leaves "Valor total" stuck until daily/qty
 * changes (which often land in the draft without a stale `taxData`).
 */
function mergeServiceValuesWithDraft(
  stepServiceValues: ServiceValues,
  draft: Partial<ServiceValues>
): ServiceValues {
  const { taxData: draftTaxData, ...draftRest } = draft;
  const merged: ServiceValues = {
    ...stepServiceValues,
    ...draftRest,
  };

  if (!draftTaxData?.tariff) {
    return merged;
  }

  const stepTaxData = stepServiceValues.taxData;
  const stepTariff = stepTaxData?.tariff;
  // Only tariff inputs are draftable; every calculated tax field stays on step.
  merged.taxData = {
    ...stepTaxData,
    tariff: {
      ...stepTariff,
      value: draftTaxData.tariff.value ?? stepTariff?.value,
      typeTax: draftTaxData.tariff.typeTax ?? stepTariff?.typeTax,
    },
  };
  return merged;
}

function tariffInputsMatch(
  a: ServiceValues["taxData"] | undefined,
  b: ServiceValues["taxData"] | undefined
): boolean {
  return (
    a?.tariff?.value === b?.tariff?.value &&
    a?.tariff?.typeTax === b?.tariff?.typeTax
  );
}

function buildServicePayload(
  serviceData: ServiceData,
  serviceValuesKey: ServiceValuesKey,
  newValues: ServiceValues,
  negotiationVersion: number
): ServiceData {
  if (serviceValuesKey === "negotiationValue") {
    const baseList = serviceData.negotiationValue ?? [];
    const targetIndex = negotiationVersion - 1;
    // Only the active negotiation entry is replaced; siblings keep their
    // original references so downstream memos that key on those refs still hit.
    const negotiationValue = baseList.map((el, index) =>
      index === targetIndex ? newValues : el
    );
    if (targetIndex >= 0 && negotiationValue[targetIndex] !== newValues) {
      negotiationValue[targetIndex] = newValues;
    }
    return { ...serviceData, negotiationValue };
  }
  return { ...serviceData, [serviceValuesKey]: newValues } as ServiceData;
}

const BudgetServiceItem: React.FC<Props> = ({
  serviceData,
  type,
  currency,
  categoryExchange,
  descriptionFocus,
  setDescriptionFocus,
  serviceFocus,
  setServiceFocus,
  mainAppliedTaxes,
  onTransferService,
}: Props) => {
  // Reads metadata only (versionInfo / disabled flag / detailedBudget /
  // activeBudgetId). The full `activeBudget` object — and especially
  // `activeBudget.budgetServices`, which changes on every keystroke in any
  // service input — is intentionally NOT consumed here so this leaf does not
  // re-render on unrelated edits in sibling services.
  const { versionInfo, isBudgetDisabled, detailedBudget, activeBudgetId } =
    useBudgetMeta();
  const { activeBudgetDispatch, runBudgetAutosave } = useBudgetActions();
  const { tabActive, event } = useProject();

  /** Valores de comparação (texto cinza abaixo dos campos) só em versões somente leitura. */
  const showComparisonValues =
    tabActive !== TabActive.BUDGET && isBudgetDisabled;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [openServiceMenu, setOpenServiceMenu] = useState(false);
  const [descriptionModal, setDescriptionModal] = useState(false);
  const [observationsModal, setObservationsModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [applicationPlaceModal, setApplicationPlaceModal] = useState(false);
  const [taxesModal, setTaxesModal] = useState(false);
  const [serviceValuesDraft, setServiceValuesDraft] = useState<
    Partial<ServiceValues>
  >({});
  const isFreeService = FREE_SERVICE_FIELD?.includes(type);
  const firstRender = useRef(false);
  const inputRef = useRef<any>(null);
  const serviceInputRef = useRef<any>(null);
  const isInputExpanded = descriptionFocus === serviceData?._id;
  const isInputServiceExpanded = serviceFocus === serviceData?._id;

  const [dailyAnchorEl, setDailyAnchorEl] = useState<HTMLElement | null>(null);
  const [openDailyMenu, setOpenDailyMenu] = useState(false);

  const [checkInAnchorEl, setCheckInAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [openCheckInCalendar, setOpenCheckInCalendar] = useState(false);
  const [checkInDate, setCheckInDate] = useState<Date | null>(null);
  const [checkInText, setCheckInText] = useState("");

  const [checkOutAnchorEl, setCheckOutAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [openCheckOutCalendar, setOpenCheckOutCalendar] = useState(false);
  const [checkOutDate, setCheckOutDate] = useState<Date | null>(null);
  const [checkOutText, setCheckOutText] = useState("");
  const dailyPopperPaperRef = useRef<HTMLDivElement | null>(null);
  const [isManualDailyEdit, setIsManualDailyEdit] = useState(false);
  /** True while seeding check-in/out from saved service props (not user edits). */
  const isSyncingDatesFromPropsRef = useRef(false);

  const verifyDates = (inDate: Date | null, outDate: Date | null) => {
    if (inDate && outDate && !isManualDailyEdit) {
      const days = daysBetweenInclusive(inDate, outDate, type);
      if (days !== serviceValues.daily) handleInputBlur(days || 1, "daily");
    }
  };

  const canSetCheckIn = (candidate: Date, currentCheckOut: Date | null) => {
    if (!currentCheckOut) return true;
    const cand = startOfDayTs(candidate);
    const out = startOfDayTs(currentCheckOut);
    if (cand > out) {
      toast.warn("Check-in não pode ser após o Check-out.");
      return false;
    }
    return true;
  };

  const canSetCheckOut = (candidate: Date, currentCheckIn: Date | null) => {
    if (!currentCheckIn) return true;
    const cand = startOfDayTs(candidate);
    const inc = startOfDayTs(currentCheckIn);
    if (cand < inc) {
      toast.warn("Check-out não pode ser antes do Check-in.");
      return false;
    }
    return true;
  };

  const { compareServiceValues, serviceValuesKey, stepServiceValues } =
    useMemo(() => {
      // These values are read-only views fed to children for display. The
      // handlers below build new objects via spread before dispatching, so
      // sharing references with `serviceData` here is safe and avoids deep
      // cloning the whole service tree on every keystroke.
      let stepServiceValues: ServiceValues = initialServiceData.budgetValues;
      let compareServiceValues = {} as ServiceValues;
      let key: ServiceValuesKey = "budgetValues";

      switch (tabActive) {
        case TabActive.NEGOTIATION:
          if (
            versionInfo?.version > 0 &&
            serviceData.negotiationValue?.[versionInfo.version - 1]
          ) {
            stepServiceValues =
              serviceData.negotiationValue[versionInfo.version - 1];
          }
          compareServiceValues =
            versionInfo?.comparisonVersion === 0
              ? serviceData.budgetValues || ({} as ServiceValues)
              : serviceData.negotiationValue?.[
                  versionInfo?.comparisonVersion - 1
                ] || ({} as ServiceValues);
          key = "negotiationValue";
          break;
        case TabActive.APPROVED:
          if (serviceData.closingValues) {
            stepServiceValues = serviceData.closingValues;
          }
          compareServiceValues =
            versionInfo?.comparisonVersion === 0
              ? serviceData.budgetValues || ({} as ServiceValues)
              : serviceData.negotiationValue?.[
                  versionInfo?.comparisonVersion - 1
                ] || ({} as ServiceValues);
          key = "closingValues";
          break;
        case TabActive.BUDGET:
        default:
          if (serviceData.budgetValues) {
            stepServiceValues = serviceData.budgetValues;
          }
          key = "budgetValues";
          break;
      }
      return {
        compareServiceValues,
        serviceValuesKey: key,
        stepServiceValues,
      };
    }, [
      tabActive,
      serviceData,
      versionInfo?.version,
      versionInfo?.comparisonVersion,
      versionInfo?.status,
    ]);

  const serviceValues = useMemo<ServiceValues>(
    () => mergeServiceValuesWithDraft(stepServiceValues, serviceValuesDraft),
    [stepServiceValues, serviceValuesDraft]
  );

  /**
   * Qtd ou Diárias em 0 zeram o valor total: o serviço fica registrado mas não
   * soma no orçamento. Sinalizamos isso ao lado do rótulo "Serviço".
   */
  const isOptionalService =
    Number(serviceValues?.quantity) === 0 || Number(serviceValues?.daily) === 0;

  const setServiceValues = useCallback(
    (next: ServiceValues | ((prev: ServiceValues) => ServiceValues)) => {
      setServiceValuesDraft((prevDraft) => {
        const currentValues = mergeServiceValuesWithDraft(
          stepServiceValues,
          prevDraft
        );
        const nextValues =
          typeof next === "function" ? next(currentValues) : next;
        return buildServiceValuesDraft(nextValues, stepServiceValues);
      });
    },
    [stepServiceValues]
  );

  const CALC_FIELD_DEBOUNCE_MS = 200;
  const calcDispatchTimerRef = useRef<number | null>(null);
  const pendingCalcValuesRef = useRef<ServiceValues | null>(null);

  const clearCalcDispatchTimer = useCallback(() => {
    if (calcDispatchTimerRef.current !== null) {
      window.clearTimeout(calcDispatchTimerRef.current);
      calcDispatchTimerRef.current = null;
    }
  }, []);

  const dispatchCalcValuesUpdate = useCallback(
    (values: ServiceValues) => {
      const updatedServiceData = buildServicePayload(
        serviceData,
        serviceValuesKey,
        values,
        versionInfo.version
      );
      startTransition(() => {
        activeBudgetDispatch({
          type: "UPDATE_CALC_VALUES",
          payload: updatedServiceData,
          versionInfo,
        });
      });
    },
    [activeBudgetDispatch, serviceData, serviceValuesKey, versionInfo]
  );

  // Keep the latest dispatcher in a ref so the debounce timer / unmount flush
  // do not re-subscribe when `serviceData` identity changes. Recreating that
  // effect used to re-flush a still-pending edit, which raced with
  // "+ Novo serviço" (blur + add) and stacked "Serviço adicionado" toasts.
  const dispatchCalcValuesUpdateRef = useRef(dispatchCalcValuesUpdate);
  dispatchCalcValuesUpdateRef.current = dispatchCalcValuesUpdate;

  const scheduleCalcValuesUpdate = useCallback(
    (values: ServiceValues) => {
      pendingCalcValuesRef.current = values;
      clearCalcDispatchTimer();
      calcDispatchTimerRef.current = window.setTimeout(() => {
        calcDispatchTimerRef.current = null;
        const pending = pendingCalcValuesRef.current;
        pendingCalcValuesRef.current = null;
        if (pending) {
          dispatchCalcValuesUpdateRef.current(pending);
        }
      }, CALC_FIELD_DEBOUNCE_MS);
    },
    [clearCalcDispatchTimer]
  );

  const flushCalcValuesUpdate = useCallback(() => {
    if (pendingCalcValuesRef.current) {
      clearCalcDispatchTimer();
      dispatchCalcValuesUpdateRef.current(pendingCalcValuesRef.current);
      pendingCalcValuesRef.current = null;
    }
  }, [clearCalcDispatchTimer]);

  useEffect(() => {
    return () => {
      if (pendingCalcValuesRef.current) {
        clearCalcDispatchTimer();
        dispatchCalcValuesUpdateRef.current(pendingCalcValuesRef.current);
        pendingCalcValuesRef.current = null;
      } else {
        clearCalcDispatchTimer();
      }
    };
  }, [clearCalcDispatchTimer]);

  const hasOverflow = useTextareaOverflow(inputRef, serviceValues?.observation);
  const serviceHasOverflow = useTextareaOverflow(
    serviceInputRef,
    serviceValues?.serviceType
  );

  // Keep only active edits in local state. On tab/version changes the row
  // should fully follow the new serviceData slice; on prop refreshes after a
  // dispatch, drop draft fields that have already been absorbed by the
  // canonical budget state.
  const prevTabActive = useRef(tabActive);
  const prevVersion = useRef(versionInfo?.version);
  const prevComparisonVersion = useRef(versionInfo?.comparisonVersion);
  useEffect(() => {
    const tabChanged = prevTabActive.current !== tabActive;
    const versionChanged = prevVersion.current !== versionInfo?.version;
    const comparisonChanged =
      prevComparisonVersion.current !== versionInfo?.comparisonVersion;
    prevTabActive.current = tabActive;
    prevVersion.current = versionInfo?.version;
    prevComparisonVersion.current = versionInfo?.comparisonVersion;

    if (tabChanged || versionChanged || comparisonChanged) {
      setServiceValuesDraft({});
      return;
    }

    setServiceValuesDraft((prevDraft) => {
      let changed = false;
      const nextDraft: Partial<ServiceValues> = {};
      (Object.keys(prevDraft) as ServiceValuesField[]).forEach((key) => {
        if (key === "taxData") {
          // Drop tariff-input draft once the reducer has absorbed it so
          // calculated currencyValue/valueBRL are not shadowed forever.
          if (tariffInputsMatch(prevDraft.taxData, stepServiceValues.taxData)) {
            changed = true;
            return;
          }
          nextDraft.taxData = prevDraft.taxData;
          return;
        }
        if (prevDraft[key] !== stepServiceValues[key]) {
          nextDraft[key] = prevDraft[key] as any;
        } else {
          changed = true;
        }
      });
      return changed ? nextDraft : prevDraft;
    });
  }, [
    tabActive,
    versionInfo?.version,
    versionInfo?.comparisonVersion,
    stepServiceValues,
  ]);

  // Atualiza o draft local imediatamente; recálculo global é debounced para
  // não bloquear a digitação em tarifa/quantidade/diárias.
  function handleFieldChange(
    value: any,
    field: keyof typeof serviceData.budgetValues
  ) {
    const newState: ServiceValues =
      field === "taxData"
        ? {
            ...serviceValues,
            taxData: {
              ...serviceValues.taxData,
              tariff: {
                ...serviceValues.taxData.tariff,
                value: value,
              },
            },
          }
        : {
            ...serviceValues,
            [field]: value,
          };
    setServiceValues(newState);
    scheduleCalcValuesUpdate(newState);
  }

  function dispatchServiceValuesUpdate(
    newValues: ServiceValues,
    field: ServiceValuesField
  ) {
    const updatedServiceData = buildServicePayload(
      serviceData,
      serviceValuesKey,
      newValues,
      versionInfo.version
    );

    if (isServiceMetadataField(field)) {
      activeBudgetDispatch({
        type: "UPDATE_SERVICE",
        payload: updatedServiceData,
        versionInfo,
        silent: true,
      });
      return;
    }

    clearCalcDispatchTimer();
    pendingCalcValuesRef.current = null;
    dispatchCalcValuesUpdate(newValues);
  }

  function handleStateChange(value: any, field: ServiceValuesField) {
    const newState: ServiceValues =
      field === "taxData"
        ? {
            ...serviceValues,
            taxData: {
              ...serviceValues.taxData,
              tariff: {
                ...serviceValues.taxData.tariff,
                value: value,
              },
            },
          }
        : {
            ...serviceValues,
            [field]: value,
          };
    setServiceValues(newState);

    // Para campos editáveis (observation, serviceType, descriptions etc.),
    // atualiza o contexto imediatamente para garantir que seja salvo
    if (isServiceMetadataField(field)) {
      dispatchServiceValuesUpdate(newState, field);
    }
  }

  function handleInputBlur(value: any, field: ServiceValuesField) {
    // Verifica se o valor realmente mudou antes de disparar o dispatch
    const currentValue = serviceValues[field];
    if (currentValue === value) {
      // Valor não mudou, não precisa disparar atualização
      return;
    }

    const newState: ServiceValues = { ...serviceValues, [field]: value };
    dispatchServiceValuesUpdate(newState, field);
    setServiceValues(newState);
  }

  function handleChangeDailyManually(
    value: any,
    field: keyof typeof serviceData.budgetValues
  ) {
    // Verifica se o valor realmente mudou antes de disparar o dispatch
    const currentValue = serviceValues[field];
    if (currentValue === value) {
      // Valor não mudou, não precisa disparar atualização
      setCheckOutDate(null);
      setCheckInDate(null);
      setCheckInText("");
      setCheckOutText("");
      return;
    }

    const newState: ServiceValues = {
      ...serviceValues,
      [field]: value,
      inputDate: "",
      outDate: "",
    };
    const updatedServiceData = buildServicePayload(
      serviceData,
      serviceValuesKey,
      newState,
      versionInfo.version
    );
    clearCalcDispatchTimer();
    pendingCalcValuesRef.current = null;
    startTransition(() => {
      activeBudgetDispatch({
        type: "UPDATE_CALC_VALUES",
        payload: updatedServiceData,
        versionInfo,
      });
    });
    setServiceValues(newState);
    setCheckOutDate(null);
    setCheckInDate(null);
    setCheckInText("");
    setCheckOutText("");
  }

  function handleDescriptionChange(descriptions: Descriptions) {
    let newState = { ...serviceValues };
    newState = {
      ...newState,
      descriptions: descriptions,
    };
    setServiceValues(newState);
  }

  function handleObservationsChange(value: string) {
    setServiceValues({ ...serviceValues, internalObservation: value });
  }

  function handleDeleteService(): void {
    Swal.fire({
      title: "Tem certeza?",
      text: "Você está prestes a apagar um serviço",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      cancelButtonText: "Não, cancelar",
      confirmButtonText: "Sim, prossiga",
    }).then((result) => {
      if (result.isConfirmed) {
        // Apenas o reducer deve alterar activeBudget. Mutar + SAVE antes do
        // recálculo enviava budgetValuesTotal desatualizado à API e o retorno
        // NEW_ACTIVE_BUDGET sobrescrevia os totais corretos (categoria/fornecedor).
        activeBudgetDispatch({
          type: "DELETE_SERVICE",
          payload: serviceData._id,
          versionInfo,
        });
        if (activeBudgetId) {
          window.setTimeout(() => {
            void runBudgetAutosave("manual");
          }, 0);
        }
        Swal.fire({
          title: "Apagado",
          text: "O serviço foi apagado",
          icon: "success",
        });
      }
    });
  }

  function submitForm(valuesOverride?: Partial<ServiceValues>) {
    const valuesToSubmit: ServiceValues = {
      ...serviceValues,
      ...valuesOverride,
    };

    if (valuesOverride) {
      setServiceValues(valuesToSubmit);
    }

    const updatedServiceData = buildServicePayload(
      serviceData,
      serviceValuesKey,
      valuesToSubmit,
      versionInfo.version
    );

    if (valuesOverride) {
      activeBudgetDispatch({
        type: "UPDATE_CALC_VALUES",
        payload: updatedServiceData,
        versionInfo,
      });
      return;
    }

    activeBudgetDispatch({
      type: "UPDATE_SERVICE",
      payload: updatedServiceData,
      versionInfo,
    });
    if (activeBudgetId) {
      window.setTimeout(() => {
        void runBudgetAutosave("manual");
      }, 0);
    }
  }

  const isCortesy =
    serviceValues?.taxData?.tariff.typeTax === typeTaxEnum.CORTESY;
  const isIncluded =
    serviceValues?.taxData?.tariff.typeTax === typeTaxEnum.INCLUDED;
  const isAcClient =
    serviceValues?.taxData?.tariff.typeTax === typeTaxEnum.AC_CLIENT;

  const handleUpdateUnitValue = (field: keyof DefaultTax, value: any) => {
    // Verifica se o valor realmente mudou antes de disparar o dispatch
    const currentValue = serviceValues?.taxData?.tariff?.[field];
    if (currentValue === value) {
      // Valor não mudou, não precisa disparar atualização
      return;
    }

    // Usar serviceValues (estado local) como base para preservar campos editáveis
    const newServiceValues: ServiceValues = {
      ...serviceValues,
      taxData: {
        ...serviceValues.taxData,
        tariff: {
          ...serviceValues.taxData.tariff,
          [field]: value,
        },
      },
    };

    clearCalcDispatchTimer();
    pendingCalcValuesRef.current = null;
    setServiceValues(newServiceValues);
    dispatchCalcValuesUpdate(newServiceValues);
  };

  useEffect(() => {
    const isActive = descriptionFocus === serviceData._id;
    if (isActive && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
      const el = inputRef.current;
      const len = el.value?.length ?? 0;
      try {
        el.setSelectionRange(len, len);
      } catch {}
    }
  }, [descriptionFocus, serviceData._id]);

  useEffect(() => {
    const isActive = serviceFocus === serviceData._id;
    if (isActive && serviceInputRef.current) {
      serviceInputRef.current.focus({ preventScroll: true });
      const el = serviceInputRef.current;
      const len = el.value?.length ?? 0;
      try {
        el.setSelectionRange(len, len);
      } catch {}
    }
  }, [serviceFocus, serviceData._id]);

  useEffect(() => {
    if (checkInDate) {
      setCheckInText(formatBRDate(checkInDate));
    }
  }, [checkInDate]);

  useEffect(() => {
    if (checkOutDate) {
      setCheckOutText(formatBRDate(checkOutDate));
    }
  }, [checkOutDate]);

  useEffect(() => {
    if (serviceValues.outDate && !checkOutDate) {
      isSyncingDatesFromPropsRef.current = true;
      setCheckOutDate(new Date(serviceValues.outDate));
    }
  }, [serviceValues.outDate]);

  useEffect(() => {
    if (serviceValues.inputDate && !checkInDate) {
      isSyncingDatesFromPropsRef.current = true;
      setCheckInDate(new Date(serviceValues.inputDate));
    }
  }, [serviceValues.inputDate]);

  // Re-derive daily only on user date changes — not when seeding from saved
  // props on category open (that was rewriting totals via UPDATE_CALC_VALUES).
  useEffect(() => {
    if (!checkInDate || !checkOutDate) return;
    if (isSyncingDatesFromPropsRef.current) {
      isSyncingDatesFromPropsRef.current = false;
      return;
    }
    verifyDates(checkInDate, checkOutDate);
  }, [checkInDate, checkOutDate]);

  const serviceFieldLabel = (
    <Box
      component="span"
      display={"inline-flex"}
      alignItems={"center"}
      gap={0.75}
    >
      Serviço
      {isOptionalService && (
        <Tooltip title="Qtd ou Diárias em 0: o serviço fica registrado, mas não soma no total do orçamento.">
          <Chip
            component="span"
            label="Opcional — não contabilizado"
            size="small"
            sx={{
              height: "20px",
              fontSize: "12px",
              fontWeight: 500,
              // O rótulo herda o line-height do Typography/InputLabel em volta,
              // o que desalinha o texto dentro da pill. Zeramos e deixamos o
              // flex do Chip centralizar.
              lineHeight: 1,
              color: "#6D28D9",
              backgroundColor: "#EDE9FE",
              border: "none",
              borderRadius: "10px",
              "& .MuiChip-label": {
                px: "10px",
                py: 0,
                lineHeight: 1,
              },
            }}
          />
        </Tooltip>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: "4px",
        backgroundColor: "white",
      }}
    >
      <DescriptionModal
        isOpen={descriptionModal}
        onClose={() => setDescriptionModal(false)}
        descriptions={serviceValues.descriptions}
        onChange={handleDescriptionChange}
        onConfirm={submitForm}
      />
      <ObservationsModal
        isOpen={observationsModal}
        onClose={() => setObservationsModal(false)}
        serviceObservations={serviceValues.internalObservation ?? ""}
        onChange={handleObservationsChange}
        onConfirm={submitForm}
      />
      <ApplicationPlaceModal
        isOpen={applicationPlaceModal}
        serviceValues={serviceValues}
        compareServiceValues={compareServiceValues}
        onClose={() => setApplicationPlaceModal(false)}
        onChange={handleStateChange}
        onConfirm={submitForm}
      />
      <TaxesModal
        isOpen={taxesModal}
        serviceValues={serviceValues}
        onClose={() => setTaxesModal(false)}
        onChange={handleStateChange}
        onConfirm={submitForm}
        mainAppliedTaxes={mainAppliedTaxes}
        exchangeCurrency={currency}
        categoryExchange={categoryExchange}
        eventScope={event?.eventScope}
      />

      <Box display={"flex"} gap={3} alignItems={"center"} flexDirection={"row"}>
        <Box
          width={"100%"}
          flexDirection={"row"}
          display={"flex"}
          alignItems={"flex-start"}
          gap={2}
          flex={1}
        >
          <Box display={"flex"} flexDirection={"row"} flex={1}>
            {isFreeService ? (
              <LabeledInput
                label={serviceFieldLabel}
                placeholder="Especifique"
                multiline
                disablePaste={false}
                disabled={isBudgetDisabled}
                maxRows={serviceFocus === serviceData?._id ? 25 : 1}
                value={serviceValues?.serviceType}
                helperText={
                  showComparisonValues
                    ? compareServiceValues?.serviceType
                    : undefined
                }
                onChange={(e) => handleInputBlur(e.target.value, "serviceType")}
                onFocus={() => {
                  setServiceFocus(serviceData?._id);
                }}
                onBlur={(e) => {
                  handleInputBlur(e.target.value, "serviceType");
                  setServiceFocus(null);
                }}
                inputRef={serviceInputRef}
                endAdornment={
                  serviceHasOverflow ? (
                    isInputServiceExpanded ? (
                      <MdExpandLess
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          (
                            serviceInputRef.current as HTMLTextAreaElement | null
                          )?.blur();
                          setServiceFocus(null);
                        }}
                      />
                    ) : (
                      <MdExpandMore
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setServiceFocus(serviceData?._id)}
                      />
                    )
                  ) : null
                }
              />
            ) : (
              <Box
                display={"flex"}
                flexDirection={"column"}
                width={"100%"}
                marginTop={"5px"}
              >
                <Box>
                  <Typography variant="caption" style={{ marginBottom: "3px" }}>
                    {serviceFieldLabel}
                  </Typography>
                </Box>
                <Select
                  size="small"
                  onChange={(e) => {
                    handleInputBlur(e.target.value, "serviceType");
                  }}
                  value={serviceValues?.serviceType || "none"}
                  fullWidth
                  disabled={isBudgetDisabled}
                >
                  <MenuItem value="none" disabled>
                    Clique para selecionar
                  </MenuItem>
                  {ServiceOptions[type].map((el: any) => {
                    return (
                      <MenuItem value={el.value} key={el.value}>
                        {el.text}
                      </MenuItem>
                    );
                  })}
                </Select>
                {showComparisonValues && (
                  <FormHelperText>
                    {
                      ServiceOptions[type].find(
                        (el: any) =>
                          el.value === compareServiceValues?.serviceType
                      )?.text
                    }
                  </FormHelperText>
                )}
              </Box>
            )}
          </Box>

          <Box display={"flex"} flex={1} sx={{ minWidth: 0 }}>
            <LabeledInputFull
              label="Descrição do serviço"
              disablePaste={false}
              placeholder="Especifique"
              multiline
              disabled={isBudgetDisabled}
              maxRows={descriptionFocus === serviceData?._id ? 25 : 1}
              value={serviceValues?.observation}
              helperText={
                showComparisonValues
                  ? compareServiceValues?.observation
                  : undefined
              }
              onChange={(e) => handleStateChange(e.target.value, "observation")}
              onFocus={() => {
                setDescriptionFocus(serviceData?._id);
              }}
              onBlur={(e) => {
                setDescriptionFocus(null);
                handleInputBlur(e.target.value, "observation");
              }}
              inputRef={inputRef}
              endAdornment={
                hasOverflow ? (
                  isInputExpanded ? (
                    <MdExpandLess
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        (
                          inputRef.current as HTMLTextAreaElement | null
                        )?.blur();
                        setDescriptionFocus(null);
                      }}
                    />
                  ) : (
                    <MdExpandMore
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setDescriptionFocus(serviceData?._id)}
                    />
                  )
                ) : null
              }
            />
          </Box>

          <Box display={"flex"} flex={0.3}>
            <LabeledInput
              value={serviceValues?.quantity}
              helperText={
                showComparisonValues && compareServiceValues?.quantity != null
                  ? String(compareServiceValues.quantity)
                  : undefined
              }
              onChange={(e) => {
                handleFieldChange(Number(e.target.value), "quantity");
              }}
              onBlur={() => {
                flushCalcValuesUpdate();
              }}
              label={`Qtd`}
              disabled={isBudgetDisabled}
            />
          </Box>

          <Box display={"flex"} flex={0.3}>
            <LabeledInput
              onChange={(e) => {
                const newValue = Number(e.target.value);
                setIsManualDailyEdit(true);
                handleFieldChange(newValue, "daily");
              }}
              onBlur={(e) => {
                const next = e.relatedTarget as Node | null;

                if (next && dailyPopperPaperRef.current?.contains(next)) return;

                flushCalcValuesUpdate();

                if (isManualDailyEdit && (checkInDate || checkOutDate)) {
                  setCheckOutDate(null);
                  setCheckInDate(null);
                  setCheckInText("");
                  setCheckOutText("");
                  handleInputBlur("", "inputDate");
                  handleInputBlur("", "outDate");
                }

                setOpenCheckInCalendar(false);
                setOpenCheckOutCalendar(false);
                setOpenDailyMenu(false);
                setDailyAnchorEl(null);
                setCheckInAnchorEl(null);
                setCheckOutAnchorEl(null);
              }}
              onFocus={(e: any) => {
                if (isBudgetDisabled) return;
                const el = (e?.currentTarget as HTMLElement) || null;
                setDailyAnchorEl(el);
                setOpenDailyMenu(true);
              }}
              value={serviceValues?.daily}
              helperText={
                showComparisonValues && compareServiceValues?.daily != null
                  ? String(compareServiceValues.daily)
                  : undefined
              }
              label={`Diárias`}
              disabled={isBudgetDisabled}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();

                  setOpenCheckInCalendar(false);
                  setOpenCheckOutCalendar(false);
                  setOpenDailyMenu(false);

                  setDailyAnchorEl(null);
                  setCheckInAnchorEl(null);
                  setCheckOutAnchorEl(null);

                  (e.currentTarget as HTMLElement)?.blur?.();
                }
              }}
            />

            <Popper
              open={openDailyMenu && !!dailyAnchorEl && !isBudgetDisabled}
              anchorEl={dailyAnchorEl}
              placement="bottom-start"
              style={{ zIndex: 1500 }}
              modifiers={[
                { name: "offset", options: { offset: [0, 8] } },
                { name: "preventOverflow", options: { padding: 8 } },
              ]}
            >
              <ClickAwayListener
                onClickAway={(event) => {
                  const target = event.target as Node;
                  if (
                    dailyAnchorEl?.contains(target) ||
                    checkInAnchorEl?.contains(target) ||
                    checkOutAnchorEl?.contains(target)
                  )
                    return;
                  setOpenCheckInCalendar(false);
                  setOpenCheckOutCalendar(false);
                  setOpenDailyMenu(false);
                  setDailyAnchorEl(null);
                  setCheckInAnchorEl(null);
                  setCheckOutAnchorEl(null);
                }}
              >
                <Paper
                  ref={dailyPopperPaperRef}
                  elevation={3}
                  sx={{
                    p: 1.5,
                    minWidth: 260,
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mb: 1, fontSize: "16px" }}
                  >
                    Data de entrada (check-in)
                  </Typography>

                  <TextField
                    size="small"
                    fullWidth
                    placeholder="dd/mm/aaaa"
                    value={checkInText}
                    onChange={(e) => {
                      const masked = applyDateMask(e.target.value);
                      setCheckInText(masked);
                      if (masked === "") {
                        setCheckInDate(null);
                        handleInputBlur("", "inputDate");
                        setIsManualDailyEdit(false);
                      }
                      const parsed = parseBRDate(masked);
                      if (parsed) {
                        setCheckInDate(parsed);
                        setOpenCheckInCalendar(false);
                        setIsManualDailyEdit(false);
                      }
                    }}
                    onFocus={(e) => {
                      setCheckInAnchorEl(e.currentTarget);
                      setOpenCheckInCalendar(true);
                      setOpenCheckOutCalendar(false);
                    }}
                    onBlur={() => {
                      if (checkInText === "") {
                        setCheckInDate(null);
                        handleInputBlur("", "inputDate");
                        return;
                      }
                      const parsed = parseBRDate(checkInText);
                      if (parsed && canSetCheckIn(parsed, checkOutDate)) {
                        setCheckInDate(parsed);
                        handleInputBlur(parsed, "inputDate");
                        setIsManualDailyEdit(false);
                      }
                    }}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const parsed = parseBRDate(checkInText);
                        if (parsed && canSetCheckIn(parsed, checkOutDate)) {
                          setCheckInDate(parsed);
                          setOpenCheckInCalendar(false);
                          handleInputBlur(parsed, "inputDate");
                        } else if (!parsed && checkInText.length > 0) {
                          toast.warn(
                            "Data inválida. Use o formato dd/mm/aaaa."
                          );
                        }
                      }
                      if (e.key === "Escape") {
                        e.preventDefault();
                        e.stopPropagation();

                        setOpenCheckInCalendar(false);
                        setOpenCheckOutCalendar(false);
                        setOpenDailyMenu(false);

                        setDailyAnchorEl(null);
                        setCheckInAnchorEl(null);
                        setCheckOutAnchorEl(null);

                        (e.currentTarget as HTMLElement)?.blur?.();
                      }
                    }}
                    inputProps={{ maxLength: 10 }}
                  />

                  <Popper
                    open={openCheckInCalendar && !!checkInAnchorEl}
                    anchorEl={checkInAnchorEl}
                    placement="bottom-start"
                    style={{ zIndex: 1600 }}
                    modifiers={[
                      { name: "offset", options: { offset: [0, 8] } },
                      { name: "preventOverflow", options: { padding: 8 } },
                    ]}
                  >
                    <Paper elevation={4} sx={{ p: 1, borderRadius: 1 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DateCalendar
                          value={checkInDate}
                          onChange={(newValue) => {
                            const next = (newValue as Date | null) ?? null;
                            if (next && !canSetCheckIn(next, checkOutDate)) {
                              setOpenCheckInCalendar(false);
                              return;
                            }
                            setCheckInDate(next);
                            setOpenCheckInCalendar(false);
                            setIsManualDailyEdit(false);
                            handleInputBlur(newValue, "inputDate");
                          }}
                        />
                      </LocalizationProvider>
                    </Paper>
                  </Popper>

                  <Box mt={1.5}>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mb: 1, fontSize: "16px" }}
                    >
                      Data de saída (check-out)
                    </Typography>

                    <TextField
                      size="small"
                      fullWidth
                      placeholder="dd/mm/aaaa"
                      value={checkOutText}
                      onChange={(e) => {
                        const masked = applyDateMask(e.target.value);
                        setCheckOutText(masked);
                        if (masked === "") {
                          setCheckOutDate(null);
                          handleInputBlur("", "outDate");
                          setIsManualDailyEdit(false);
                        }
                        const parsed = parseBRDate(masked);
                        if (parsed) {
                          setCheckOutDate(parsed);
                          setOpenCheckOutCalendar(false);
                          setIsManualDailyEdit(false);
                        }
                      }}
                      onFocus={(e) => {
                        setCheckOutAnchorEl(e.currentTarget);
                        setOpenCheckOutCalendar(true);
                        setOpenCheckInCalendar(false);
                      }}
                      onBlur={() => {
                        if (checkOutText === "") {
                          setCheckOutDate(null);
                          handleInputBlur("", "outDate");
                          return;
                        }
                        const parsed = parseBRDate(checkOutText);
                        if (parsed && canSetCheckOut(parsed, checkInDate)) {
                          setCheckOutDate(parsed);
                          handleInputBlur(parsed, "outDate");
                          setIsManualDailyEdit(false);
                        }
                      }}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const parsed = parseBRDate(checkOutText);
                          if (parsed && canSetCheckOut(parsed, checkInDate)) {
                            setCheckOutDate(parsed);
                            setOpenCheckOutCalendar(false);
                            handleInputBlur(parsed, "outDate");
                          } else if (!parsed && checkOutText.length > 0) {
                            toast.warn(
                              "Data inválida. Use o formato dd/mm/aaaa."
                            );
                          }
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          e.stopPropagation();

                          setOpenCheckInCalendar(false);
                          setOpenCheckOutCalendar(false);
                          setOpenDailyMenu(false);

                          setDailyAnchorEl(null);
                          setCheckInAnchorEl(null);
                          setCheckOutAnchorEl(null);

                          (e.currentTarget as HTMLElement)?.blur?.();
                        }
                      }}
                      inputProps={{ maxLength: 10 }}
                    />

                    <Popper
                      open={openCheckOutCalendar && !!checkOutAnchorEl}
                      anchorEl={checkOutAnchorEl}
                      placement="bottom-start"
                      style={{ zIndex: 1600 }}
                      modifiers={[
                        { name: "offset", options: { offset: [0, 8] } },
                        { name: "preventOverflow", options: { padding: 8 } },
                      ]}
                    >
                      <Paper elevation={4} sx={{ p: 1, borderRadius: 1 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DateCalendar
                            value={checkOutDate}
                            onChange={(newValue) => {
                              const next = (newValue as Date | null) ?? null;
                              if (next && !canSetCheckOut(next, checkInDate)) {
                                setOpenCheckOutCalendar(false);
                                return;
                              }
                              setCheckOutDate(next);
                              setOpenCheckOutCalendar(false);
                              setIsManualDailyEdit(false);
                              handleInputBlur(newValue, "outDate");
                            }}
                            shouldDisableDate={(date) => {
                              if (!checkInDate) return false;
                              return (
                                startOfDayTs(date) < startOfDayTs(checkInDate)
                              );
                            }}
                            defaultCalendarMonth={checkInDate || undefined}
                          />
                        </LocalizationProvider>
                      </Paper>
                    </Popper>
                  </Box>
                </Paper>
              </ClickAwayListener>
            </Popper>
          </Box>

          <Box
            display={"flex"}
            flex={1}
            flexDirection={"column"}
            sx={{ pointerEvents: isBudgetDisabled ? "none" : "auto" }}
          >
            {!serviceValues?.taxData?.tariff ||
            (!serviceValues?.taxData?.tariff?.typeTax &&
              !isCortesy &&
              !isIncluded &&
              !isAcClient) ||
            (!isCortesy && !isIncluded && !isAcClient) ? (
              <Box mt={"8px"}>
                <UnitValueInput
                  key={`${serviceData._id}-${versionInfo?.version}-${tabActive}-${serviceValuesKey}`}
                  label="Valor unitário"
                  prefix={currency}
                  value={serviceValues.taxData.tariff.value}
                  onChange={(value: number) => {
                    handleFieldChange(value, "taxData");
                  }}
                  onBlur={() => {
                    flushCalcValuesUpdate();
                  }}
                  helperText={
                    showComparisonValues && compareServiceValues?.taxData
                      ? formatUnitValue(
                          compareServiceValues.taxData.tariff.value,
                          currency
                        )
                      : undefined
                  }
                  InputProps={{
                    endAdornment: (
                      <Box sx={{ display: "flex", flexDirection: "row" }}>
                        <Tooltip
                          title={
                            <span style={{ fontSize: "16px" }}>Cortesia</span>
                          }
                          placement="top"
                          disableInteractive
                          PopperProps={{
                            modifiers: [
                              {
                                name: "offset",
                                options: {
                                  offset: [0, 2],
                                },
                              },
                            ],
                          }}
                        >
                          <Box
                            sx={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              ml: 0.5,
                              mt: -0.5,
                              p: 0.5,
                            }}
                            onClick={() =>
                              handleUpdateUnitValue(
                                "typeTax",
                                isCortesy
                                  ? typeTaxEnum.DAILY
                                  : typeTaxEnum.CORTESY
                              )
                            }
                          >
                            <MdCardGiftcard
                              style={{
                                color: isBudgetDisabled
                                  ? "gray"
                                  : isCortesy
                                  ? "#1976D2"
                                  : "black",
                                pointerEvents: "none",
                              }}
                            />
                          </Box>
                        </Tooltip>

                        <Tooltip
                          title={
                            <span style={{ fontSize: "16px" }}>Incluso</span>
                          }
                          placement="top"
                          disableInteractive
                          PopperProps={{
                            modifiers: [
                              {
                                name: "offset",
                                options: {
                                  offset: [0, 2],
                                },
                              },
                            ],
                          }}
                        >
                          <Box
                            sx={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              ml: 0.5,
                              mt: -0.5,
                              p: 0.5,
                            }}
                            onClick={() =>
                              handleUpdateUnitValue(
                                "typeTax",
                                isIncluded
                                  ? typeTaxEnum.DAILY
                                  : typeTaxEnum.INCLUDED
                              )
                            }
                          >
                            <FiCheckSquare
                              style={{
                                color: isBudgetDisabled
                                  ? "gray"
                                  : isIncluded
                                  ? "#1976D2"
                                  : "black",
                                pointerEvents: "none",
                              }}
                            />
                          </Box>
                        </Tooltip>

                        <Tooltip
                          title={
                            <span style={{ fontSize: "16px" }}>
                              A/C Cliente
                            </span>
                          }
                          placement="top"
                          disableInteractive
                          PopperProps={{
                            modifiers: [
                              {
                                name: "offset",
                                options: {
                                  offset: [0, 2],
                                },
                              },
                            ],
                          }}
                        >
                          <Box
                            sx={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              ml: 0.5,
                              mt: -0.5,
                              p: 0.5,
                            }}
                            onClick={() =>
                              handleUpdateUnitValue(
                                "typeTax",
                                isAcClient
                                  ? typeTaxEnum.DAILY
                                  : typeTaxEnum.AC_CLIENT
                              )
                            }
                          >
                            <FiUserCheck
                              style={{
                                color: isBudgetDisabled
                                  ? "gray"
                                  : isAcClient
                                  ? "#1976D2"
                                  : "black",
                                pointerEvents: "none",
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    ),
                  }}
                  disabled={
                    isCortesy || isIncluded || isAcClient || isBudgetDisabled
                  }
                />
              </Box>
            ) : (
              <Box
                display={"flex"}
                flex={1}
                flexDirection={"column"}
                mt={"-1px"}
              >
                <LabeledInput
                  value={
                    isCortesy
                      ? "Cortesia"
                      : isIncluded
                      ? "Incluso"
                      : "A/C Cliente"
                  }
                  helperText={
                    showComparisonValues
                      ? compareServiceValues?.daily
                      : undefined
                  }
                  label={`Valor unitário`}
                  disabled={isCortesy || isIncluded || isAcClient}
                  endAdornment={
                    isCortesy ? (
                      <MdCardGiftcard
                        style={{
                          cursor: "pointer",
                          color: isBudgetDisabled
                            ? "gray"
                            : isCortesy
                            ? "#1976D2"
                            : "black",
                          marginLeft: 8,
                          fontSize: 17,
                        }}
                        onClick={() =>
                          handleUpdateUnitValue(
                            "typeTax",
                            isCortesy ? typeTaxEnum.DAILY : typeTaxEnum.CORTESY
                          )
                        }
                      />
                    ) : isIncluded ? (
                      <FiCheckSquare
                        style={{
                          cursor: "pointer",
                          color: isBudgetDisabled
                            ? "gray"
                            : isIncluded
                            ? "#1976D2"
                            : "black",
                          marginLeft: 8,
                          fontSize: 17,
                        }}
                        onClick={() =>
                          handleUpdateUnitValue(
                            "typeTax",
                            isIncluded
                              ? typeTaxEnum.DAILY
                              : typeTaxEnum.INCLUDED
                          )
                        }
                      />
                    ) : (
                      <FiUserCheck
                        style={{
                          cursor: "pointer",
                          color: isBudgetDisabled
                            ? "gray"
                            : isAcClient
                            ? "#1976D2"
                            : "black",
                          marginLeft: 8,
                          fontSize: 17,
                        }}
                        onClick={() =>
                          handleUpdateUnitValue(
                            "typeTax",
                            isAcClient
                              ? typeTaxEnum.DAILY
                              : typeTaxEnum.AC_CLIENT
                          )
                        }
                      />
                    )
                  }
                />
              </Box>
            )}
          </Box>

          <Box display={"flex"} flex={0.5}>
            <LabeledInput
              disabled
              value={formatCurrencyValue(
                serviceValues?.taxData?.tariff.currencyValue || 0,
                currency
              )}
              helperText={
                showComparisonValues && tabActive !== TabActive.BUDGET
                  ? formatCurrencyValue(
                      compareServiceValues?.taxData?.tariff.currencyValue || 0,
                      currency
                    )
                  : undefined
              }
              label={`Valor total`}
              customHeight={40}
            />
          </Box>

          <Box
            display={"flex"}
            flex={0.2}
            alignItems={"center"}
            justifyContent={"center"}
            marginTop={4.5}
            gap={1}
          >
            {(hasCustomTaxes(
              serviceValues?.appliedTaxes,
              mainAppliedTaxes,
              serviceValues?.appliedValues?.markupOverride,
              serviceValues?.appliedValues?.commissionOverride,
              Boolean(serviceValues?.appliedValues?.supplierTributeOverride) &&
                !serviceValues?.appliedValues?.markupOverride,
              { currency, eventScope: event?.eventScope }
            ) ||
              serviceValues?.exchangeOverride) && (
              <Tooltip
                title="Este serviço possui taxas ou câmbio diferentes do padrão"
                placement="top"
              >
                <Box
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                  onClick={() => setTaxesModal(true)}
                >
                  <MdWarning color="#FF9800" size={20} />
                </Box>
              </Tooltip>
            )}
            <Box
              sx={{
                cursor: !isBudgetDisabled ? "pointer" : "default",
                display: "flex",
                alignItems: "center",
              }}
              onClick={() => {
                if (isBudgetDisabled) return;
                setAnchorEl(null);
                setOpenServiceMenu(false);
                handleDeleteService();
              }}
            >
              <IoIosCloseCircle
                color={isBudgetDisabled ? "gray" : "#D32F2F"}
                size={20}
              />
            </Box>
            {!!stepServiceValues?.internalObservation?.trim() && (
              <Tooltip title="Observações internas" placement="top">
                <Box
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#00CFE8",
                    borderRadius: "50%",
                    padding: "4px",
                  }}
                  onClick={() => setObservationsModal(true)}
                >
                  <ViewObsIcon width={12} height={12} />
                </Box>
              </Tooltip>
            )}
            <Box
              data-testid="service-menu-trigger"
              sx={{
                cursor: !isBudgetDisabled ? "pointer" : "default",
                display: "flex",
                alignItems: "center",
              }}
              onClick={(e) => {
                if (isBudgetDisabled) return;
                setAnchorEl(e.currentTarget);
                setOpenServiceMenu(true);
              }}
            >
              <BsThreeDotsVertical />
            </Box>
          </Box>
        </Box>
      </Box>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={openServiceMenu}
        onClose={() => {
          setAnchorEl(null);
          setOpenServiceMenu(false);
        }}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        autoFocus={false}
      >
        {[
          "ATTRACTION",
          "BRINDES_INCENTIVOS_PREMIACOES",
          "CENOGRAPHY_DECORATION",
          "EQUIPMENTS",
        ].includes(type) && (
          <MenuItem
            autoFocus={false}
            onClick={() => {
              setAnchorEl(null);
              setOpenServiceMenu(false);
              setApplicationPlaceModal(true);
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ApplicationPlaceIcon width={16} height={16} />
              <span>Local de aplicação</span>
            </Box>
          </MenuItem>
        )}
        <MenuItem
          autoFocus={false}
          onClick={() => {
            setAnchorEl(null);
            setOpenServiceMenu(false);
            setDescriptionModal(true);
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DescriptionIcon width={16} height={16} />
            <span>Descritivos</span>
          </Box>
        </MenuItem>
        <MenuItem
          autoFocus={false}
          onClick={() => {
            setAnchorEl(null);
            setOpenServiceMenu(false);
            setTaxesModal(true);
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PercentIcon width={16} height={16} />
            <span>Taxas</span>
          </Box>
        </MenuItem>
        <MenuItem
          autoFocus={false}
          onClick={() => {
            setAnchorEl(null);
            setOpenServiceMenu(false);
            setObservationsModal(true);
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WriteObsIcon width={16} height={16} />
            <span>Obs. internas</span>
          </Box>
        </MenuItem>
        {!!onTransferService && (
          <MenuItem
            autoFocus={false}
            onClick={() => {
              setAnchorEl(null);
              setOpenServiceMenu(false);
              onTransferService(serviceData);
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <MdOutlineSwapHoriz size={16} />
              <span>Transferir</span>
            </Box>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

// React.memo bail-out predicate. Returning `true` tells React to skip the
// next render. We compare every prop by reference: the parent now passes a
// new `serviceData` reference only when this specific service was mutated
// (Stage 1 stopped the wholesale `JSON.parse(JSON.stringify(...))` on
// keystrokes, so siblings keep their original refs through the reducer's
// shallow spread). Combined with the slice-hook migration above —
// `BudgetServiceItem` only consumes the meta + actions slices, neither of
// which changes on a service edit — this turns each row into a true island
// that re-renders only for its own data and tab/version transitions.
const areEqual = (prevProps: Props, nextProps: Props) => {
  return (
    prevProps.type === nextProps.type &&
    prevProps.index === nextProps.index &&
    prevProps.title === nextProps.title &&
    prevProps.currency === nextProps.currency &&
    prevProps.categoryExchange === nextProps.categoryExchange &&
    prevProps.serviceData === nextProps.serviceData &&
    prevProps.descriptionFocus === nextProps.descriptionFocus &&
    prevProps.serviceFocus === nextProps.serviceFocus &&
    prevProps.setDescriptionFocus === nextProps.setDescriptionFocus &&
    prevProps.setServiceFocus === nextProps.setServiceFocus &&
    prevProps.mainAppliedTaxes === nextProps.mainAppliedTaxes &&
    prevProps.onTransferService === nextProps.onTransferService
  );
};

export default React.memo(BudgetServiceItem, areEqual);
